import React, { useEffect, useRef, useState } from 'react';
import { activityService } from '../../services';
import {
  Search, ChevronRight, ChevronDown, X, Camera, Flag,
  ShieldCheck, Clock, Layers, Users, Wrench, Truck,
  Download, BarChart2, AlertTriangle, CloudRain, Zap, Plus, Check
} from 'lucide-react';

// ── Types ────────────────────────────────────────────────────
interface Activity {
  activity_id: number;
  act_code: string;
  activity_code?: string;
  activity_name: string;
  sequence_order?: number;
  quantity_unit?: string;
  photo_required?: string;         // MANDATORY/OPTIONAL/NOT_REQUIRED
  activity_type?: string;
  quality_check_required?: boolean;
  duration_optimistic?: number;
  duration_likely?: number;
  duration_pessimistic?: number;
  duration_expected?: number;
  allowed_roles?: string[];
  customer_visible?: boolean;
  is_milestone?: boolean;
  is_critical_path?: boolean;
  is_weather_dependent?: boolean;
}
interface SubProcess {
  sub_process_id: number; sub_process_code: string;
  sub_process_name: string; activities: Activity[];
}
interface Process {
  process_id: number; process_code: string;
  process_name: string; sub_processes: SubProcess[];
}
interface Stage {
  stage_id: number; stage_code: string; stage_name: string;
  billing_trigger?: boolean; customer_visible?: boolean;
  processes: Process[];
}

const TYPE_COLORS: Record<string,string> = {
  STRUCTURAL:'bg-blue-100 text-blue-700',
  FINISHING: 'bg-amber-100 text-amber-700',
  MEP:       'bg-cyan-100 text-cyan-700',
  EXTERNAL:  'bg-emerald-100 text-emerald-700',
  ADMIN:     'bg-gray-100 text-gray-600',
};
const PHOTO_COLORS: Record<string,string> = {
  MANDATORY:    'text-rose-500',
  OPTIONAL:     'text-amber-400',
  NOT_REQUIRED: 'text-gray-300',
};

const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const fmt = (n: any) => n ? Number(n).toLocaleString() : '0';

const exportCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row =>
    keys.map(k => `"${String(row[k]??'').replace(/"/g,'""')}"`).join(',')
  )].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
  a.download = filename; a.click();
};

// ── Activity Row ──────────────────────────────────────────────
const ActivityRow = ({ act, onClick }: { act: Activity; onClick: ()=>void }) => {
  const code = act.act_code || act.activity_code || '';
  const photoClass = PHOTO_COLORS[act.photo_required||''] || 'text-gray-300';
  return (
    <button onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-blue-50 text-left transition-colors group">
      <span className="text-xs font-mono text-gray-400 w-24 flex-shrink-0">{code}</span>
      <span className="text-sm text-gray-700 flex-1 min-w-0 truncate group-hover:text-blue-700">{act.activity_name}</span>
      <div className="flex items-center gap-1 flex-shrink-0">
        {act.photo_required && act.photo_required !== 'NOT_REQUIRED' &&
          <Camera size={12} className={photoClass}/>}
        {act.is_milestone   && <Flag size={12} className="text-purple-500"/>}
        {act.quality_check_required && <ShieldCheck size={12} className="text-blue-500"/>}
        {act.is_critical_path && <Zap size={12} className="text-red-500"/>}
        {act.is_weather_dependent && <CloudRain size={12} className="text-sky-400"/>}
        {act.activity_type && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${TYPE_COLORS[act.activity_type]||'bg-gray-100 text-gray-500'}`}>
            {act.activity_type}
          </span>
        )}
      </div>
      <ChevronRight size={14} className="text-gray-300 group-hover:text-blue-400 flex-shrink-0"/>
    </button>
  );
};

// ── Activity Detail Drawer ────────────────────────────────────
const ActivityDrawer = ({ activityId, onClose }: { activityId: number; onClose: ()=>void }) => {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    activityService.getActivity(activityId)
      .then(r => { if (alive) setDetail(r.data.data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activityId]);

  const Attr = ({ label, value }: { label: string; value: any }) => (
    <div className="bg-gray-50 rounded-lg px-3 py-2">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-xs font-semibold text-gray-800">{value || '—'}</p>
    </div>
  );

  const Section = ({ icon, title, count, children }: any) => (
    <div className="mb-5">
      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        {icon}{title} <span className="text-gray-300 font-normal">({count})</span>
      </h4>
      {count === 0 ? <p className="text-xs text-gray-400 italic">None mapped</p> : children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}/>
      <div className="relative w-full max-w-md bg-white shadow-xl h-full overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs text-gray-400 font-mono">{detail?.act_code || '...'}</p>
            <h3 className="text-base font-bold text-gray-900 truncate">{detail?.activity_name || 'Loading...'}</h3>
            {detail && (
              <p className="text-xs text-gray-400 mt-0.5">
                {detail.stage_name} → {detail.process_name} → {detail.sub_process_name}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 flex-shrink-0">
            <X size={18}/>
          </button>
        </div>

        {loading ? (
          <div className="p-5 space-y-3">
            {[...Array(4)].map((_,i) => <div key={i} className="bg-gray-100 rounded h-16 animate-pulse"/>)}
          </div>
        ) : detail ? (
          <div className="p-5">
            {/* Badges */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {detail.photo_required === 'MANDATORY' && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 font-medium">
                  <Camera size={11}/>Photo Mandatory</span>)}
              {detail.is_milestone && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 font-medium">
                  <Flag size={11}/>Milestone</span>)}
              {detail.quality_check_required && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
                  <ShieldCheck size={11}/>QC Gate</span>)}
              {detail.customer_visible && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">
                  <Users size={11}/>Customer Visible</span>)}
              {detail.is_critical_path && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-medium">
                  <Zap size={11}/>Critical Path</span>)}
              {detail.is_weather_dependent && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 font-medium">
                  <CloudRain size={11}/>Weather Dependent</span>)}
            </div>

            {/* Attributes grid */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <Attr label="Unit of Measure" value={detail.quantity_unit}/>
              <Attr label="Activity Type"   value={detail.activity_type}/>
              <Attr label="Photo Required"  value={detail.photo_required}/>
              <Attr label="Progress Type"   value={detail.default_progress_type}/>
            </div>

            {/* Duration */}
            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <p className="text-xs font-bold text-gray-500 mb-2 flex items-center gap-1">
                <Clock size={12}/> Duration (days)
              </p>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label:'Optimistic', val:detail.duration_optimistic },
                  { label:'Likely',     val:detail.duration_likely },
                  { label:'Pessimistic',val:detail.duration_pessimistic },
                  { label:'Expected',   val:detail.duration_expected },
                ].map(s => (
                  <div key={s.label} className="text-center">
                    <p className="text-sm font-bold text-gray-800">{s.val||'—'}</p>
                    <p className="text-xs text-gray-400">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Roles */}
            {detail.allowed_roles && detail.allowed_roles.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-gray-500 mb-2">Who Can Mark</p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.allowed_roles.map((r: string) => (
                    <span key={r} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{r}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Resource Maps */}
            <Section icon={<Layers size={12}/>} title="Materials" count={detail.materials?.length||0}>
              <div className="space-y-1">
                {detail.materials?.map((m: any) => (
                  <div key={m.map_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
                    <div>
                      <span className="font-medium text-gray-800">{m.mat_name||m.mat_id}</span>
                      {m.mat_category && <span className="text-gray-400 ml-2">{m.mat_category}</span>}
                    </div>
                    <div className="text-right text-gray-500">
                      {m.quantity_per_unit} {m.unit_description}
                      {m.wastage_pct > 0 && <span className="ml-1 text-amber-500">+{m.wastage_pct}% waste</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={<Users size={12}/>} title="Manpower" count={detail.manpower?.length||0}>
              <div className="space-y-1">
                {detail.manpower?.map((m: any) => (
                  <div key={m.map_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
                    <div>
                      <span className="font-medium text-gray-800">{m.skill_name||m.skill_code}</span>
                      {m.trade_category && <span className="text-gray-400 ml-2">{m.trade_category}</span>}
                    </div>
                    <div className="text-right text-gray-500">
                      {m.workers_per_unit}/unit
                      {m.is_gang_based && <span className="ml-1 text-purple-500">gang:{m.gang_size}</span>}
                      {m.current_daily_rate && <span className="ml-1 text-green-600">₹{Number(m.current_daily_rate).toLocaleString()}/day</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={<Wrench size={12}/>} title="Tools" count={detail.tools?.length||0}>
              <div className="space-y-1">
                {detail.tools?.map((t: any) => (
                  <div key={t.map_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">{t.tls_category}</span>
                      {t.is_safety_ppe && <span className="text-amber-600">🦺PPE</span>}
                      {t.is_consumable && <span className="text-blue-500">consumable</span>}
                    </div>
                    <span className="text-gray-500">{t.qty_per_worker}/worker</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={<Truck size={12}/>} title="Machinery" count={detail.machinery?.length||0}>
              <div className="space-y-1">
                {detail.machinery?.map((m: any) => (
                  <div key={m.map_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
                    <span className="font-medium text-gray-800">{m.mch_type}</span>
                    <span className="text-gray-500">
                      ×{m.quantity_needed} · {m.hours_per_day}h/day · {m.owned_or_hired}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        ) : (
          <div className="p-5 text-center text-gray-400">Activity not found.</div>
        )}
      </div>
    </div>
  );
};

// ── Stats Panel ───────────────────────────────────────────────
const StatsPanel = ({ stats }: { stats: any }) => {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-5">
      {[
        { label:'Stages',       val:stats.total_stages,              color:'bg-purple-600 text-white' },
        { label:'Processes',    val:stats.total_processes,           color:'bg-blue-600 text-white' },
        { label:'Sub-Procs',    val:stats.total_sub_processes,       color:'bg-indigo-600 text-white' },
        { label:'Activities',   val:stats.total_activities,          color:'bg-gray-700 text-white' },
        { label:'Milestones',   val:stats.milestone_activities,      color:'bg-purple-100 text-purple-800' },
        { label:'QC Gates',     val:stats.qc_activities,             color:'bg-blue-100 text-blue-800' },
      ].map(s => (
        <div key={s.label} className={`${s.color} rounded-xl p-3 text-center`}>
          <p className="text-xl font-bold">{fmt(s.val)}</p>
          <p className="text-xs mt-0.5 opacity-90">{s.label}</p>
        </div>
      ))}
    </div>
  );
};

// ── Quick-add row (single text field) ────────────────────────
const QuickAddRow = ({ placeholder, onSave, onCancel, saving }: {
  placeholder: string; onSave: (v: string) => void; onCancel: () => void; saving?: boolean;
}) => {
  const [val, setVal] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="flex items-center gap-2 py-1.5 px-3">
      <input ref={ref} value={val} onChange={e => setVal(e.target.value)}
        placeholder={placeholder}
        onKeyDown={e => { if (e.key==='Enter') onSave(val); if (e.key==='Escape') onCancel(); }}
        className="flex-1 border border-blue-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"/>
      <button onClick={() => onSave(val)} disabled={saving || !val.trim()}
        className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
        <Check size={13}/>
      </button>
      <button onClick={onCancel} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100">
        <X size={13}/>
      </button>
    </div>
  );
};

// ── Add Activity form ─────────────────────────────────────────
const AddActivityForm = ({ subCode, subId, processId, stageId, saving, onSave, onCancel }: {
  subCode: string; subId: number; processId: number; stageId: number;
  saving?: boolean; onSave: (subCode: string, subId: number, processId: number, stageId: number, fields: any) => void;
  onCancel: () => void;
}) => {
  const [f, setF] = useState({
    act_code: '', activity_name: '', activity_type: 'STRUCTURAL',
    quantity_unit: 'SFT', photo_required: 'NOT_REQUIRED',
    is_milestone: false, quality_check_required: false,
  });
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const set = (k: string, v: any) => setF(prev => ({ ...prev, [k]: v }));
  return (
    <div className="mx-3 my-2 p-3 border border-blue-200 rounded-xl bg-blue-50 space-y-2">
      <p className="text-xs font-semibold text-blue-700">New Activity</p>
      <div className="flex gap-2">
        <input ref={ref} value={f.act_code} onChange={e => set('act_code', e.target.value)}
          placeholder="Code (e.g. ACT-S063-001)"
          className="w-44 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"/>
        <input value={f.activity_name} onChange={e => set('activity_name', e.target.value)}
          placeholder="Activity name"
          className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"/>
      </div>
      <div className="flex gap-2 flex-wrap">
        <select value={f.activity_type} onChange={e => set('activity_type', e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
          {['STRUCTURAL','FINISHING','MEP','EXTERNAL','ADMIN'].map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={f.photo_required} onChange={e => set('photo_required', e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
          {['NOT_REQUIRED','OPTIONAL','MANDATORY'].map(p => <option key={p}>{p}</option>)}
        </select>
        <input value={f.quantity_unit} onChange={e => set('quantity_unit', e.target.value)}
          placeholder="Unit"
          className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none"/>
        <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
          <input type="checkbox" checked={f.is_milestone} onChange={e => set('is_milestone', e.target.checked)} className="rounded"/>
          Milestone
        </label>
        <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
          <input type="checkbox" checked={f.quality_check_required} onChange={e => set('quality_check_required', e.target.checked)} className="rounded"/>
          QC Gate
        </label>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(subCode, subId, processId, stageId, f)}
          disabled={saving || !f.act_code.trim() || !f.activity_name.trim()}
          className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1">
          <Check size={12}/> Add Activity
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-200 rounded-lg">Cancel</button>
      </div>
    </div>
  );
};

// ── Main ActivityPage ─────────────────────────────────────────
const ActivityPage = () => {
  const [tab, setTab]                 = useState<'tree'|'search'|'milestones'|'stats'>('tree');
  const [tree, setTree]               = useState<Stage[]>([]);
  const [milestones, setMilestones]   = useState<any[]>([]);
  const [stats, setStats]             = useState<any>(null);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching]     = useState(false);
  const [selectedActId, setSelectedActId] = useState<number|null>(null);
  const [openStages, setOpenStages]   = useState<number[]>([]);
  const [openProcs, setOpenProcs]     = useState<number[]>([]);
  const [openSubs, setOpenSubs]       = useState<number[]>([]);
  const [typeFilter, setTypeFilter]   = useState('');
  const [photoFilter, setPhotoFilter] = useState('');

  // ── Quick-add state ───────────────────────────────────────────
  const [addingStage, setAddingStage]     = useState(false);
  const [addingProcFor, setAddingProcFor] = useState<number|null>(null);   // stage_id
  const [addingSubFor, setAddingSubFor]   = useState<number|null>(null);   // process_id
  const [addingActFor, setAddingActFor]   = useState<{subId:number;subCode:string}|null>(null);
  const [saving, setSaving]              = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tRes, mRes, sRes] = await Promise.all([
        activityService.getTree(),
        activityService.getMilestones(),
        activityService.getStats(),
      ]);
      setTree(tRes.data.data || []);
      setMilestones(mRes.data.data || []);
      setStats(sRes.data.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const saveStage = async (name: string) => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await activityService.createStage({ stage_name: name.trim() });
      setAddingStage(false);
      await loadAll();
    } catch(e: any) { alert(e.response?.data?.message || 'Failed to create stage'); }
    finally { setSaving(false); }
  };

  const saveProcess = async (stageId: number, name: string) => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await activityService.createProcess({ stage_id: stageId, process_name: name.trim() });
      setAddingProcFor(null);
      setOpenStages(prev => prev.includes(stageId) ? prev : [...prev, stageId]);
      await loadAll();
    } catch(e: any) { alert(e.response?.data?.message || 'Failed to create process'); }
    finally { setSaving(false); }
  };

  const saveSubProcess = async (processId: number, stageId: number, name: string) => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await activityService.createSubProcess({ process_id: processId, sub_process_name: name.trim() });
      setAddingSubFor(null);
      setOpenProcs(prev => prev.includes(processId) ? prev : [...prev, processId]);
      setOpenStages(prev => prev.includes(stageId) ? prev : [...prev, stageId]);
      await loadAll();
    } catch(e: any) { alert(e.response?.data?.message || 'Failed to create sub-process'); }
    finally { setSaving(false); }
  };

  const saveActivity = async (subCode: string, subId: number, processId: number, stageId: number, fields: any) => {
    if (!fields.activity_name?.trim() || !fields.act_code?.trim()) return;
    setSaving(true);
    try {
      await activityService.createActivity({ ...fields, sub_process_code: subCode });
      setAddingActFor(null);
      setOpenSubs(prev => prev.includes(subId) ? prev : [...prev, subId]);
      setOpenProcs(prev => prev.includes(processId) ? prev : [...prev, processId]);
      setOpenStages(prev => prev.includes(stageId) ? prev : [...prev, stageId]);
      await loadAll();
    } catch(e: any) { alert(e.response?.data?.message || 'Failed to create activity'); }
    finally { setSaving(false); }
  };

  const toggle = (arr: number[], setArr: React.Dispatch<React.SetStateAction<number[]>>, id: number) =>
    setArr(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await activityService.search(search.trim());
      setSearchResults(res.data.data);
    } finally { setSearching(false); }
  };

  // Filter tree activities
  const filterAct = (act: Activity) => {
    if (typeFilter && act.activity_type !== typeFilter) return false;
    if (photoFilter && act.photo_required !== photoFilter) return false;
    return true;
  };

  // Flatten all activities for export
  const flattenTree = () => {
    const rows: any[] = [];
    tree.forEach(s => s.processes.forEach(p => p.sub_processes.forEach(sp =>
      sp.activities.forEach(a => rows.push({
        Stage:s.stage_name, Process:p.process_name, SubProcess:sp.sub_process_name,
        Code:a.act_code||a.activity_code, Name:a.activity_name,
        Type:a.activity_type, Unit:a.quantity_unit||'',
        Photo:a.photo_required, QC:a.quality_check_required?'Yes':'No',
        Milestone:a.is_milestone?'Yes':'No', CriticalPath:a.is_critical_path?'Yes':'No',
        DurationExpected:a.duration_expected||'',
        Roles:(a.allowed_roles||[]).join('|'),
      }))
    )));
    return rows;
  };

  return (
    <div className="p-6">
      {selectedActId && <ActivityDrawer activityId={selectedActId} onClose={() => setSelectedActId(null)}/>}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Activity Master</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {stats
              ? `${fmt(stats.total_stages)} stages · ${fmt(stats.total_processes)} processes · ${fmt(stats.total_sub_processes)} sub-processes · ${fmt(stats.total_activities)} activities`
              : 'Loading...'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setAddingStage(true); setTab('tree'); }}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={14}/> New Stage
          </button>
          <button onClick={() => exportCSV(flattenTree(), 'activity_master.csv')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export All
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-5">
        {([
          { key:'tree',       label:'Browse Tree' },
          { key:'search',     label:'Search' },
          { key:'milestones', label:`Milestones (${fmt(milestones.length)})` },
          { key:'stats',      label:'Stats' },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab===t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TREE TAB ── */}
      {tab === 'tree' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Types</option>
              {['STRUCTURAL','FINISHING','MEP','EXTERNAL','ADMIN'].map(t =>
                <option key={t}>{t}</option>)}
            </select>
            <select value={photoFilter} onChange={e => setPhotoFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Photo</option>
              {['MANDATORY','OPTIONAL','NOT_REQUIRED'].map(p => <option key={p}>{p}</option>)}
            </select>
            {(typeFilter || photoFilter) && (
              <button onClick={() => { setTypeFilter(''); setPhotoFilter(''); }}
                className="px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                Clear Filters
              </button>
            )}
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_,i) =>
                <div key={i} className="bg-white rounded-xl border border-gray-200 h-12 animate-pulse"/>)}
            </div>
          ) : (
            <div className="space-y-2">
              {addingStage && (
                <div className="bg-white rounded-xl border-2 border-blue-300 overflow-hidden">
                  <QuickAddRow placeholder="New stage name (e.g. Ground Beam Construction)"
                    onSave={saveStage} onCancel={() => setAddingStage(false)} saving={saving}/>
                </div>
              )}
              {tree.map(stage => {
                const sOpen = openStages.includes(stage.stage_id);
                // Count filtered activities in this stage
                const actCount = stage.processes
                  .flatMap(p => p.sub_processes.flatMap(sp => sp.activities.filter(filterAct))).length;
                if ((typeFilter || photoFilter) && actCount === 0) return null;
                return (
                  <div key={stage.stage_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center">
                      <button onClick={() => toggle(openStages, setOpenStages, stage.stage_id)}
                        className="flex-1 flex items-center gap-2 px-4 py-3 hover:bg-gray-50 text-left">
                        {sOpen ? <ChevronDown size={16} className="text-gray-400"/> : <ChevronRight size={16} className="text-gray-400"/>}
                        <Layers size={15} className="text-blue-500"/>
                        <span className="text-xs font-mono text-gray-400">{stage.stage_code}</span>
                        <span className="text-sm font-semibold text-gray-800 flex-1">{stage.stage_name}</span>
                        {stage.billing_trigger && (
                          <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">Billing</span>)}
                        {stage.customer_visible && (
                          <span className="text-xs bg-blue-50 text-blue-500 px-2 py-0.5 rounded-full">Customer</span>)}
                        <span className="text-xs text-gray-400">{stage.processes?.length||0} proc</span>
                      </button>
                      <button onClick={() => { setAddingProcFor(stage.stage_id); setOpenStages(p => p.includes(stage.stage_id)?p:[...p,stage.stage_id]); }}
                        title="Add Process"
                        className="mr-3 p-1 rounded hover:bg-blue-50 text-blue-400 hover:text-blue-600">
                        <Plus size={14}/>
                      </button>
                    </div>

                    {sOpen && (
                      <div className="border-t border-gray-100 px-2 py-2 space-y-1">
                        {addingProcFor === stage.stage_id && (
                          <div className="ml-3">
                            <QuickAddRow placeholder="New process name (e.g. Foundation Excavation)"
                              onSave={name => saveProcess(stage.stage_id, name)}
                              onCancel={() => setAddingProcFor(null)} saving={saving}/>
                          </div>
                        )}
                        {stage.processes.map(proc => {
                          const pOpen = openProcs.includes(proc.process_id);
                          const procActCount = proc.sub_processes
                            .flatMap(sp => sp.activities.filter(filterAct)).length;
                          if ((typeFilter || photoFilter) && procActCount === 0) return null;
                          return (
                            <div key={proc.process_id} className="ml-3">
                              <div className="flex items-center">
                                <button onClick={() => toggle(openProcs, setOpenProcs, proc.process_id)}
                                  className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-left">
                                  {pOpen ? <ChevronDown size={14} className="text-gray-400"/> : <ChevronRight size={14} className="text-gray-400"/>}
                                  <span className="text-xs font-mono text-gray-400">{proc.process_code}</span>
                                  <span className="text-sm font-medium text-gray-700 flex-1">{proc.process_name}</span>
                                  <span className="text-xs text-gray-400">{proc.sub_processes?.length||0} sub</span>
                                </button>
                                <button onClick={() => { setAddingSubFor(proc.process_id); setOpenProcs(p => p.includes(proc.process_id)?p:[...p,proc.process_id]); setOpenStages(p => p.includes(stage.stage_id)?p:[...p,stage.stage_id]); }}
                                  title="Add Sub-Process"
                                  className="mr-2 p-1 rounded hover:bg-blue-50 text-blue-400 hover:text-blue-600">
                                  <Plus size={13}/>
                                </button>
                              </div>

                              {pOpen && (
                                <div className="ml-5 space-y-1">
                                  {addingSubFor === proc.process_id && (
                                    <QuickAddRow placeholder="New sub-process name (e.g. Shuttering Work)"
                                      onSave={name => saveSubProcess(proc.process_id, stage.stage_id, name)}
                                      onCancel={() => setAddingSubFor(null)} saving={saving}/>
                                  )}
                                  {proc.sub_processes.map(sub => {
                                    const subOpen = openSubs.includes(sub.sub_process_id);
                                    const filteredActs = sub.activities.filter(filterAct);
                                    if ((typeFilter || photoFilter) && filteredActs.length === 0) return null;
                                    return (
                                      <div key={sub.sub_process_id}>
                                        <div className="flex items-center">
                                          <button onClick={() => toggle(openSubs, setOpenSubs, sub.sub_process_id)}
                                            className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 text-left">
                                            {subOpen ? <ChevronDown size={13} className="text-gray-400"/> : <ChevronRight size={13} className="text-gray-400"/>}
                                            <span className="text-xs font-mono text-gray-400">{sub.sub_process_code}</span>
                                            <span className="text-sm text-gray-600 flex-1">{sub.sub_process_name}</span>
                                            <span className="text-xs text-gray-400">{filteredActs.length} act</span>
                                          </button>
                                          <button
                                            onClick={() => { setAddingActFor({subId:sub.sub_process_id, subCode:sub.sub_process_code}); setOpenSubs(p => p.includes(sub.sub_process_id)?p:[...p,sub.sub_process_id]); }}
                                            title="Add Activity"
                                            className="mr-2 p-1 rounded hover:bg-blue-50 text-blue-400 hover:text-blue-600">
                                            <Plus size={12}/>
                                          </button>
                                        </div>
                                        {subOpen && (
                                          <div className="ml-5 border-l-2 border-gray-100 pl-2 py-1">
                                            {addingActFor?.subId === sub.sub_process_id && (
                                              <AddActivityForm
                                                subCode={sub.sub_process_code}
                                                subId={sub.sub_process_id}
                                                processId={proc.process_id}
                                                stageId={stage.stage_id}
                                                saving={saving}
                                                onSave={saveActivity}
                                                onCancel={() => setAddingActFor(null)}/>
                                            )}
                                            {filteredActs.map(act => (
                                              <ActivityRow key={act.activity_id} act={act}
                                                onClick={() => setSelectedActId(act.activity_id)}/>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── SEARCH TAB ── */}
      {tab === 'search' && (
        <>
          <form onSubmit={handleSearch} className="flex gap-2 mb-5">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search activities, stages, processes..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Search</button>
          </form>
          <div className="bg-white rounded-xl border border-gray-200 p-2">
            {searching ? (
              <div className="space-y-1 p-2">
                {[...Array(5)].map((_,i) => <div key={i} className="bg-gray-100 rounded h-9 animate-pulse"/>)}
              </div>
            ) : searchResults.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">
                {search ? 'No results.' : 'Type a query and hit Search.'}
              </p>
            ) : (
              <div className="space-y-0.5">
                <div className="flex items-center justify-between px-3 py-1">
                  <p className="text-xs text-gray-400">{searchResults.length} result(s)</p>
                  <button onClick={() => exportCSV(searchResults.map(a => ({
                    Code:a.act_code, Name:a.activity_name, Type:a.activity_type,
                    Stage:a.stage_name, Process:a.process_name, Sub:a.sub_process_name,
                  })), 'search_results.csv')}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                    <Download size={11}/> Export
                  </button>
                </div>
                {searchResults.map(act => (
                  <ActivityRow key={act.activity_id} act={act}
                    onClick={() => setSelectedActId(act.activity_id)}/>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── MILESTONES TAB ── */}
      {tab === 'milestones' && (
        <div className="bg-white rounded-xl border border-gray-200 p-2">
          {loading ? (
            <div className="space-y-1 p-2">
              {[...Array(5)].map((_,i) => <div key={i} className="bg-gray-100 rounded h-9 animate-pulse"/>)}
            </div>
          ) : milestones.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No milestone activities defined.</p>
          ) : (
            <div className="space-y-0.5">
              <div className="flex items-center justify-between px-3 py-1">
                <p className="text-xs text-gray-400">{milestones.length} milestones</p>
                <button onClick={() => exportCSV(milestones.map(m => ({
                  Code:m.act_code, Name:m.activity_name, Stage:m.stage_name,
                  Process:m.process_name, Sub:m.sub_process_name,
                  CustomerVisible:m.customer_visible?'Yes':'No', BillingTrigger:m.billing_trigger?'Yes':'No',
                  DurationExpected:m.duration_expected||''
                })), 'milestones.csv')}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                  <Download size={11}/> Export
                </button>
              </div>
              {milestones.map(act => (
                <div key={act.activity_id} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-purple-50 cursor-pointer"
                  onClick={() => setSelectedActId(act.activity_id)}>
                  <Flag size={14} className="text-purple-500 flex-shrink-0"/>
                  <span className="text-xs font-mono text-gray-400 w-24 flex-shrink-0">{act.act_code}</span>
                  <span className="text-sm text-gray-700 flex-1">{act.activity_name}</span>
                  <span className="text-xs text-gray-400">{act.stage_name}</span>
                  {act.billing_trigger && (
                    <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">Billing</span>)}
                  {act.customer_visible && (
                    <span className="text-xs bg-blue-50 text-blue-500 px-2 py-0.5 rounded-full">Customer</span>)}
                  {act.duration_expected && (
                    <span className="text-xs text-gray-400">{act.duration_expected}d</span>)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── STATS TAB ── */}
      {tab === 'stats' && (
        <div className="space-y-5">
          <StatsPanel stats={stats}/>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label:'Mandatory Photo', val:stats?.mandatory_photo_activities, color:'text-rose-600', bg:'bg-rose-50', icon:<Camera size={16}/> },
              { label:'QC Gates',        val:stats?.qc_activities,              color:'text-blue-600', bg:'bg-blue-50',  icon:<ShieldCheck size={16}/> },
              { label:'Critical Path',   val:stats?.critical_path_activities,   color:'text-red-600',  bg:'bg-red-50',   icon:<Zap size={16}/> },
              { label:'Customer Visible',val:stats?.customer_visible_activities, color:'text-emerald-600', bg:'bg-emerald-50', icon:<Users size={16}/> },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
                <div className={`${s.color} mb-1`}>{s.icon}</div>
                <p className={`text-2xl font-bold ${s.color}`}>{fmt(s.val)}</p>
                <p className="text-xs text-gray-600 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          {stats && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Hierarchy</h3>
              <div className="space-y-2">
                {[
                  { label:'Stages',          val:stats.total_stages,          total:stats.total_stages },
                  { label:'Processes',        val:stats.total_processes,       total:stats.total_processes },
                  { label:'Sub-Processes',    val:stats.total_sub_processes,   total:stats.total_sub_processes },
                  { label:'Activities',       val:stats.total_activities,      total:stats.total_activities },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-3">
                    <span className="text-xs text-gray-600 w-32">{s.label}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full"
                        style={{width:`${Math.min((Number(s.val)/Number(stats.total_activities||1))*100,100)}%`}}/>
                    </div>
                    <span className="text-xs font-bold text-gray-700 w-10 text-right">{fmt(s.val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityPage;
